import { theme as antdTheme, type ThemeConfig } from 'antd';
import {
  COLOR_PRIMARY,
  COLOR_PRIMARY_HOVER,
  COLOR_PRIMARY_ACTIVE,
  COLOR_PRIMARY_LIGHT,
  COLOR_SUCCESS,
  COLOR_WARNING,
  COLOR_ERROR,
  COLOR_RED_SOFT,
  COLOR_INFO,
  COLOR_PURPLE_DEEP,
  COLOR_BG_BASE,
  COLOR_BG_SUBTLE,
  COLOR_BG_MUTED,
  COLOR_BG_ELEVATED,
  COLOR_BG_ELEVATED_SOLID,
  COLOR_BG_SIDEBAR,
  COLOR_BG_MODAL,
  COLOR_TEXT_PRIMARY,
  COLOR_TEXT_SECONDARY,
  COLOR_TEXT_TERTIARY,
  COLOR_BORDER_DEFAULT,
  COLOR_BORDER_SUBTLE,
  COLOR_BORDER_STRONG,
  COLOR_PURPLE_LIGHT_SOFT,
  COLOR_PURPLE_LIGHT_SOFT_STRONG,
  COLOR_PURPLE_LIGHT_2,
  COLOR_YELLOW,
  FONT_FAMILY_SANS,
  FONT_SIZE_XS,
  FONT_SIZE_SM,
  FONT_SIZE_BASE,
  FONT_SIZE_LG,
  FONT_SIZE_XL,
  FONT_SIZE_MODAL_TITLE,
  FONT_SIZE_2XL,
  FONT_SIZE_3XL,
  LINE_HEIGHT_NORMAL,
  LINE_HEIGHT_TIGHT,
  SPACING_1,
  SPACING_2,
  SPACING_2_5,
  SPACING_3,
  SPACING_4,
  SPACING_6,
  SPACING_8,
  RADIUS_SM,
  RADIUS_DEFAULT,
  RADIUS_MD,
  RADIUS_LG,
  RADIUS_3XL,
  RADIUS_FULL,
  SHADOW_SM,
  SHADOW_DEFAULT,
  SHADOW_MD,
  TRANSITION_DURATION_DEFAULT,
  TRANSITION_DURATION_FAST,
  HEADER_HEIGHT,
  CONTROL_HEIGHT,
  CONTROL_HEIGHT_SM,
  CONTROL_HEIGHT_LG,
  CONTROL_HEIGHT_XS,
} from './tokens';

function toNumber(value: string): number {
  if (value.endsWith('rem')) return parseFloat(value) * 16;
  if (value.endsWith('px')) return parseFloat(value);
  if (value.endsWith('ms')) return parseFloat(value);
  return parseFloat(value);
}

export const theme: ThemeConfig = {
  algorithm: antdTheme.darkAlgorithm,
  hashed: true,
  // Exposes AntD's design tokens as --ant-* CSS custom properties (e.g.
  // --ant-color-bg-container), so plain CSS/SCSS can reference them directly.
  cssVar: { prefix: 'ant' },

  token: {
    colorPrimary: COLOR_PRIMARY,
    colorPrimaryHover: COLOR_PRIMARY_HOVER,
    colorPrimaryActive: COLOR_PRIMARY_ACTIVE,
    colorPrimaryBg: COLOR_PRIMARY_LIGHT,
    colorPrimaryBgHover: COLOR_PURPLE_LIGHT_SOFT_STRONG,
    colorPrimaryBorder: COLOR_PRIMARY,
    colorPrimaryBorderHover: COLOR_PRIMARY_HOVER,
    colorPrimaryText: COLOR_PRIMARY,
    colorPrimaryTextHover: COLOR_PRIMARY_HOVER,
    colorPrimaryTextActive: COLOR_PRIMARY_ACTIVE,
    colorLink: COLOR_PRIMARY,
    colorLinkHover: COLOR_PURPLE_LIGHT_2,
    colorLinkActive: COLOR_PRIMARY_ACTIVE,

    colorSuccess: COLOR_SUCCESS,
    colorWarning: COLOR_WARNING,
    colorError: COLOR_ERROR,
    colorInfo: COLOR_INFO,

    colorBgBase: COLOR_BG_BASE,
    colorBgLayout: COLOR_BG_BASE,
    colorBgContainer: COLOR_BG_ELEVATED,
    colorBgElevated: COLOR_BG_ELEVATED_SOLID,
    colorBgSpotlight: COLOR_BG_MUTED,
    // Standardized modal/drawer overlay — flat black, no brand tint. See
    // "Standardize modals & drawers" in apps/web/CLAUDE.md.
    colorBgMask: 'rgba(0, 0, 0, 0.5)',

    colorTextBase: COLOR_TEXT_PRIMARY,
    colorText: COLOR_TEXT_PRIMARY,
    colorTextSecondary: COLOR_TEXT_SECONDARY,
    colorTextTertiary: COLOR_TEXT_TERTIARY,
    colorTextQuaternary: 'rgba(246, 246, 246, 0.28)',

    colorBorder: COLOR_BORDER_DEFAULT,
    colorBorderSecondary: COLOR_BORDER_SUBTLE,

    fontFamily: FONT_FAMILY_SANS,
    fontFamilyCode:
      "'JetBrains Mono', 'Fira Code', 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace",
    fontSize: toNumber(FONT_SIZE_BASE),
    fontSizeSM: toNumber(FONT_SIZE_SM),
    fontSizeLG: toNumber(FONT_SIZE_LG),
    fontSizeXL: toNumber(FONT_SIZE_XL),
    fontSizeHeading1: toNumber(FONT_SIZE_3XL),
    fontSizeHeading2: toNumber(FONT_SIZE_2XL),
    fontSizeHeading3: toNumber(FONT_SIZE_XL),
    fontSizeHeading4: toNumber(FONT_SIZE_LG),
    fontSizeHeading5: toNumber(FONT_SIZE_BASE),
    lineHeight: parseFloat(LINE_HEIGHT_NORMAL),

    borderRadius: toNumber(RADIUS_DEFAULT),
    borderRadiusSM: toNumber(RADIUS_SM),
    borderRadiusLG: toNumber(RADIUS_MD),
    borderRadiusXS: 2,

    padding: toNumber(SPACING_4),
    paddingXS: toNumber(SPACING_2),
    paddingSM: toNumber(SPACING_3),
    paddingLG: toNumber(SPACING_6),
    paddingXL: toNumber(SPACING_8),
    margin: toNumber(SPACING_4),
    marginXS: toNumber(SPACING_2),
    marginSM: toNumber(SPACING_3),
    marginLG: toNumber(SPACING_6),
    marginXL: toNumber(SPACING_8),

    controlHeight: toNumber(CONTROL_HEIGHT),
    controlHeightSM: toNumber(CONTROL_HEIGHT_SM),
    controlHeightLG: toNumber(CONTROL_HEIGHT_LG),
    controlHeightXS: toNumber(CONTROL_HEIGHT_XS),

    motionDurationFast: TRANSITION_DURATION_FAST,
    motionDurationMid: TRANSITION_DURATION_DEFAULT,
    motionDurationSlow: '0.3s',

    boxShadow: SHADOW_DEFAULT,
    boxShadowSecondary: SHADOW_MD,

    wireframe: false,
  },

  components: {
    Layout: {
      headerBg: COLOR_BG_BASE,
      headerHeight: toNumber(HEADER_HEIGHT),
      headerPadding: `0 ${SPACING_6}`,
      bodyBg: COLOR_BG_BASE,
      siderBg: COLOR_BG_SUBTLE,
      footerBg: COLOR_BG_BASE,
    },

    Menu: {
      itemBg: 'transparent',
      itemHoverBg: COLOR_BG_MUTED,
      itemSelectedBg: COLOR_PURPLE_LIGHT_SOFT,
      itemSelectedColor: COLOR_PRIMARY,
      itemColor: COLOR_TEXT_SECONDARY,
      itemHoverColor: COLOR_TEXT_PRIMARY,
      horizontalItemSelectedColor: COLOR_PRIMARY,
      horizontalItemSelectedBg: 'transparent',
      subMenuItemBg: 'transparent',
      itemBorderRadius: toNumber(RADIUS_LG),
      itemMarginInline: toNumber(SPACING_2),
      itemPaddingInline: toNumber(SPACING_4),
      iconMarginInlineEnd: toNumber(SPACING_2),
      iconSize: 18,
      collapsedIconSize: 20,
    },

    Button: {
      // Set explicitly at the component level: AntD's dark-theme algorithm
      // always recomputes the *global* colorPrimary alias from a generated
      // palette (see genColorMapToken), which can diverge from the literal
      // seed we pass in. Component-level tokens bypass that and win outright.
      colorPrimary: COLOR_PRIMARY,
      colorPrimaryHover: COLOR_PRIMARY_HOVER,
      colorPrimaryActive: COLOR_PRIMARY_ACTIVE,
      borderRadius: toNumber(RADIUS_DEFAULT),
      borderRadiusSM: toNumber(RADIUS_SM),
      borderRadiusLG: toNumber(RADIUS_MD),
      controlHeight: toNumber(CONTROL_HEIGHT),
      controlHeightSM: toNumber(CONTROL_HEIGHT_SM),
      controlHeightLG: toNumber(CONTROL_HEIGHT_LG),
      fontWeight: 500,
      primaryShadow: 'none',
      defaultShadow: 'none',
      dangerShadow: 'none',
      paddingInline: toNumber(SPACING_4),
      paddingInlineSM: toNumber(SPACING_3),
      paddingInlineLG: toNumber(SPACING_6),
      defaultBg: 'transparent',
      defaultBorderColor: COLOR_BORDER_DEFAULT,
      defaultColor: COLOR_TEXT_PRIMARY,
      defaultHoverBg: COLOR_BG_MUTED,
      defaultHoverBorderColor: COLOR_BORDER_DEFAULT,
      defaultHoverColor: COLOR_TEXT_PRIMARY,
      borderColorDisabled: COLOR_BORDER_DEFAULT,
    },

    Input: {
      borderRadius: toNumber(RADIUS_DEFAULT),
      borderRadiusSM: toNumber(RADIUS_SM),
      borderRadiusLG: toNumber(RADIUS_MD),
      controlHeight: toNumber(CONTROL_HEIGHT),
      controlHeightSM: toNumber(CONTROL_HEIGHT_SM),
      controlHeightLG: toNumber(CONTROL_HEIGHT_LG),
      paddingInline: toNumber(SPACING_3),
      paddingInlineSM: toNumber(SPACING_2),
      paddingInlineLG: toNumber(SPACING_4),
      activeBorderColor: COLOR_PRIMARY,
      hoverBorderColor: COLOR_PRIMARY_HOVER,
      activeShadow: `0 0 0 3px ${COLOR_PURPLE_LIGHT_SOFT_STRONG}`,
      // AntD's dark-theme algorithm recomputes the global colorErrorOutline
      // token (same class of bug as Button's colorPrimary above), which
      // resolves to a near-black ring instead of a translucent red. Setting
      // this component-level token bypasses that broken derivation.
      errorActiveShadow: `0 0 0 3px ${COLOR_RED_SOFT}`,
      colorBgContainer: COLOR_BG_SUBTLE,
      // Slightly more visible than the app-wide default border — matches the
      // auth concept's field borders, which read clearly against the dark surface.
      colorBorder: COLOR_BORDER_STRONG,
    },

    Select: {
      borderRadius: toNumber(RADIUS_DEFAULT),
      controlHeight: toNumber(CONTROL_HEIGHT),
      controlHeightSM: toNumber(CONTROL_HEIGHT_SM),
      controlHeightLG: toNumber(CONTROL_HEIGHT_LG),
      optionSelectedBg: COLOR_PURPLE_LIGHT_SOFT,
      optionActiveBg: COLOR_BG_MUTED,
      colorBgContainer: COLOR_BG_SUBTLE,
      colorBgElevated: COLOR_BG_ELEVATED_SOLID,
    },

    Card: {
      borderRadiusLG: toNumber(RADIUS_LG),
      paddingLG: toNumber(SPACING_6),
      boxShadow: SHADOW_SM,
      boxShadowSecondary: SHADOW_DEFAULT,
      headerBg: 'transparent',
      actionsBg: 'transparent',
      colorBgContainer: COLOR_BG_ELEVATED,
    },

    Modal: {
      // 24px flat — explicit per-app request, not the shared RADIUS_LG every
      // other elevated surface (Card, Popover) uses.
      borderRadiusLG: toNumber(RADIUS_3XL),
      paddingLG: toNumber(SPACING_6),
      paddingMD: toNumber(SPACING_4),
      titleFontSize: toNumber(FONT_SIZE_MODAL_TITLE),
      // Standardized modal surface — solid opaque purple (the composited
      // equivalent of --color-bg-elevated over --color-bg-base), no
      // per-modal variation. The border (no AntD token for it) and the
      // mask's blur (also tokenless) are added globally in global.scss instead.
      headerBg: COLOR_BG_MODAL,
      contentBg: COLOR_BG_MODAL,
      footerBg: COLOR_BG_MODAL,
    },

    Table: {
      borderRadius: toNumber(RADIUS_MD),
      headerBg: COLOR_BG_SUBTLE,
      headerColor: COLOR_TEXT_PRIMARY,
      headerSortActiveBg: COLOR_BG_MUTED,
      headerSortHoverBg: COLOR_BG_MUTED,
      rowHoverBg: COLOR_BG_MUTED,
      rowSelectedBg: COLOR_PURPLE_LIGHT_SOFT,
      rowSelectedHoverBg: COLOR_PURPLE_LIGHT_SOFT_STRONG,
      cellPaddingBlock: toNumber(SPACING_4),
      cellPaddingInline: toNumber(SPACING_4),
      headerSplitColor: 'transparent',
      colorBgContainer: COLOR_BG_ELEVATED_SOLID,
    },

    Form: {
      labelFontSize: toNumber(FONT_SIZE_SM),
      labelColor: COLOR_TEXT_PRIMARY,
      labelRequiredMarkColor: COLOR_ERROR,
      itemMarginBottom: toNumber(SPACING_6),
      verticalLabelPadding: `0 0 ${SPACING_2}`,
    },

    Tabs: {
      cardBg: COLOR_BG_ELEVATED,
      itemColor: COLOR_TEXT_SECONDARY,
      itemHoverColor: COLOR_TEXT_PRIMARY,
      itemSelectedColor: COLOR_PRIMARY,
      inkBarColor: COLOR_PRIMARY,
      horizontalItemPadding: `${SPACING_3} ${SPACING_4}`,
      horizontalItemGutter: toNumber(SPACING_8),
    },

    Notification: {
      width: 384,
      borderRadiusLG: toNumber(RADIUS_FULL),
      colorBgElevated: COLOR_BG_SIDEBAR,
      fontSize: toNumber(FONT_SIZE_XS),
      fontSizeLG: toNumber(FONT_SIZE_SM),
      lineHeightLG: toNumber(LINE_HEIGHT_TIGHT),
      paddingMD: toNumber(SPACING_2_5),
      paddingContentHorizontalLG: toNumber(SPACING_2_5),
    },

    Message: {
      contentBg: COLOR_BG_SIDEBAR,
      contentPadding: `${SPACING_3} ${SPACING_4}`,
    },

    Tooltip: {
      borderRadius: toNumber(RADIUS_DEFAULT),
      paddingSM: toNumber(SPACING_3),
      paddingXS: toNumber(SPACING_2),
      fontSize: toNumber(FONT_SIZE_SM),
      colorBgSpotlight: COLOR_PURPLE_DEEP,
      sizePopupArrow: 0,
    },

    Popover: {
      borderRadiusLG: toNumber(RADIUS_LG),
      padding: toNumber(SPACING_4),
      colorBgElevated: COLOR_BG_BASE,
    },

    Drawer: {
      paddingLG: toNumber(SPACING_6),
      footerPaddingBlock: toNumber(SPACING_4),
      footerPaddingInline: toNumber(SPACING_6),
      // Same standardized surface as Modal — see the comment there. Drawer's
      // border (per-placement — only the edge facing the page content) and
      // its mask's blur are added globally in global.scss.
      colorBgElevated: COLOR_BG_SIDEBAR,
    },

    Badge: {
      statusSize: 8,
      indicatorHeight: 20,
      indicatorHeightSM: 16,
      dotSize: 8,
      textFontSize: toNumber(FONT_SIZE_SM),
      textFontWeight: 600,
    },

    Avatar: {
      borderRadius: toNumber(RADIUS_DEFAULT),
      groupBorderColor: COLOR_BG_ELEVATED_SOLID,
    },

    Tag: {
      borderRadiusSM: 9999,
      defaultBg: COLOR_PURPLE_LIGHT_SOFT,
      defaultColor: COLOR_PURPLE_LIGHT_2,
    },

    Pagination: {
      borderRadius: toNumber(RADIUS_DEFAULT),
      itemSize: 32,
      itemSizeSM: 28,
      itemActiveBg: COLOR_PURPLE_LIGHT_SOFT,
      itemLinkBg: 'transparent',
      itemInputBg: COLOR_BG_SUBTLE,
    },

    Progress: {
      defaultColor: COLOR_PRIMARY,
      remainingColor: COLOR_BORDER_SUBTLE,
    },

    Skeleton: {
      gradientFromColor: COLOR_BG_SUBTLE,
      gradientToColor: COLOR_BG_MUTED,
    },

    Spin: {
      dotSize: 20,
      dotSizeSM: 14,
      dotSizeLG: 32,
    },

    Empty: {
      colorText: COLOR_TEXT_TERTIARY,
      colorTextDisabled: COLOR_TEXT_TERTIARY,
    },
    Result: {
      titleFontSize: toNumber(FONT_SIZE_XL),
      subtitleFontSize: toNumber(FONT_SIZE_BASE),
    },
    Divider: {
      colorSplit: COLOR_BORDER_DEFAULT,
      textPaddingInline: toNumber(SPACING_4),
    },
    Breadcrumb: {
      itemColor: COLOR_TEXT_TERTIARY,
      lastItemColor: COLOR_TEXT_PRIMARY,
      linkColor: COLOR_TEXT_SECONDARY,
      linkHoverColor: COLOR_PRIMARY,
      separatorColor: COLOR_TEXT_TERTIARY,
    },
    Dropdown: {
      borderRadiusLG: toNumber(RADIUS_MD),
      paddingBlock: toNumber(SPACING_1),
      controlItemBgHover: COLOR_BG_MUTED,
      controlItemBgActive: COLOR_PURPLE_LIGHT_SOFT,
      colorBgElevated: COLOR_BG_ELEVATED_SOLID,
    },
    Steps: {
      iconSize: 32,
      iconSizeSM: 24,
      dotSize: 8,
      navArrowColor: COLOR_TEXT_TERTIARY,
      dotCurrentSize: 10,
    },
    Timeline: {
      dotBg: COLOR_BG_ELEVATED_SOLID,
      dotBorderWidth: 2,
      itemPaddingBottom: toNumber(SPACING_6),
      tailColor: COLOR_BORDER_DEFAULT,
    },
    Collapse: {
      headerBg: COLOR_BG_SUBTLE,
      contentBg: COLOR_BG_ELEVATED_SOLID,
      contentPadding: `${SPACING_4} ${SPACING_6}`,
      headerPadding: `${SPACING_3} ${SPACING_4}`,
      borderRadiusLG: toNumber(RADIUS_MD),
    },
    Alert: {
      borderRadiusLG: toNumber(RADIUS_MD),
      withDescriptionPadding: `${SPACING_4} ${SPACING_4}`,
      defaultPadding: `${SPACING_2} ${SPACING_4}`,
    },
    Checkbox: {
      borderRadiusSM: toNumber(RADIUS_SM),
    },
    Radio: {
      radioSize: 18,
      dotSize: 10,
    },
    Switch: {
      trackHeight: 24,
      trackMinWidth: 44,
      trackPadding: 3,
      // antd derives handleSize from the global fontSize/lineHeight tokens with a
      // hardcoded padding of 2, ignoring the trackPadding override above — without
      // this, the handle circle ends up 3px from the track's top edge but only 1px
      // from the bottom, since its height doesn't shrink to match trackPadding: 3.
      handleSize: 18,
    },
    Slider: {
      railBg: COLOR_BORDER_DEFAULT,
      railHoverBg: COLOR_BORDER_SUBTLE,
      trackBg: COLOR_PRIMARY,
      trackHoverBg: COLOR_PRIMARY_HOVER,
      handleColor: COLOR_PRIMARY,
      handleActiveColor: COLOR_PRIMARY_ACTIVE,
      dotBorderColor: COLOR_BORDER_DEFAULT,
      dotActiveBorderColor: COLOR_PRIMARY,
    },
    Rate: {
      starSize: 20,
      starColor: COLOR_YELLOW,
      starHoverScale: 'scale(1.1)',
    },
    Upload: {
      actionsColor: COLOR_TEXT_TERTIARY,
    },
    Calendar: {
      itemActiveBg: COLOR_PURPLE_LIGHT_SOFT,
      fullBg: COLOR_BG_ELEVATED_SOLID,
      fullPanelBg: COLOR_BG_ELEVATED_SOLID,
    },
    DatePicker: {
      cellWidth: 36,
      cellHeight: 28,
      cellActiveWithRangeBg: COLOR_PURPLE_LIGHT_SOFT,
      cellHoverBg: COLOR_BG_MUTED,
      colorBgContainer: COLOR_BG_SUBTLE,
      colorBgElevated: COLOR_BG_ELEVATED_SOLID,
      activeBorderColor: COLOR_PRIMARY,
      hoverBorderColor: COLOR_PRIMARY_HOVER,
      activeShadow: `0 0 0 3px ${COLOR_PURPLE_LIGHT_SOFT_STRONG}`,
    },
    InputNumber: {
      colorBgContainer: COLOR_BG_SUBTLE,
      activeBorderColor: COLOR_PRIMARY,
      hoverBorderColor: COLOR_PRIMARY_HOVER,
      activeShadow: `0 0 0 3px ${COLOR_PURPLE_LIGHT_SOFT_STRONG}`,
    },
    Tree: {
      directoryNodeSelectedBg: COLOR_PURPLE_LIGHT_SOFT,
      directoryNodeSelectedColor: COLOR_PRIMARY,
      nodeHoverBg: COLOR_BG_MUTED,
      nodeSelectedBg: COLOR_PURPLE_LIGHT_SOFT,
    },
    Transfer: {
      headerHeight: 48,
      itemHeight: 40,
      listWidth: 200,
      listWidthLG: 280,
    },
  },
};

export const lightTheme: ThemeConfig = {
  ...theme,
  algorithm: antdTheme.defaultAlgorithm,
};

export const compactTheme: ThemeConfig = {
  ...theme,
  token: {
    ...theme.token,
    fontSize: 13,
    controlHeight: toNumber(CONTROL_HEIGHT_SM),
    controlHeightSM: toNumber(CONTROL_HEIGHT_XS),
    controlHeightLG: toNumber(CONTROL_HEIGHT),
    padding: toNumber(SPACING_3),
    paddingSM: toNumber(SPACING_2),
    paddingLG: toNumber(SPACING_4),
  },
};

export const darkTheme = theme;

export default theme;
